import { useEffect, useState, useCallback } from 'react';
import { automationApi, type ShippingProvider } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { PageHeader, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { RefreshCw, Plus, Truck, Cpu, MessageCircle, Save } from 'lucide-react';

export default function AutomationSettingsPage() {
  const [providers, setProviders] = useState<ShippingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [newProvider, setNewProvider] = useState({ provider_name: '', provider_code: '', is_enabled: false, is_auto_shipping: false });

  const load = useCallback(async () => {
    try {
      const [p] = await Promise.all([
        automationApi.shippingProviders(),
      ]);
      if (p.success) setProviders(p.data);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addProvider = async () => {
    if (!newProvider.provider_name || !newProvider.provider_code) {
      toast.error('Name and code are required');
      return;
    }
    try {
      await automationApi.createShippingProvider(newProvider);
      toast.success('Provider added');
      setShowAddProvider(false);
      setNewProvider({ provider_name: '', provider_code: '', is_enabled: false, is_auto_shipping: false });
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const recalcSegments = async () => {
    try {
      await automationApi.recalculateSegments();
      toast.success('Segments recalculated');
    } catch (e: any) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="Automation Settings" description="Configure automation providers and rules" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="Automation Settings"
        description="Configure shipping providers, WhatsApp, AI, and automation rules"
        action={<Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>}
      />

      {/* WhatsApp Status */}
      <Card>
        <CardHeader
          title="WhatsApp Integration"
          icon={<MessageCircle className="h-4 w-4 text-emerald-400" />}
          description="Manage your WhatsApp Cloud API connection"
        />
        <CardBody>
          <p className="text-sm text-zinc-400">
            WhatsApp connection is managed in{' '}
            <a href="/whatsapp" className="text-blue-400 hover:underline">WhatsApp Settings</a>.
            Ensure your phone number is connected and verified before enabling automation flows.
          </p>
        </CardBody>
      </Card>

      {/* AI Configuration */}
      <Card>
        <CardHeader
          title="AI Configuration"
          icon={<Cpu className="h-4 w-4 text-purple-400" />}
          description="AI provider settings for conversation and campaign personalization"
        />
        <CardBody>
          <div className="space-y-3">
            <div className="text-sm text-zinc-400">
              AI provider (Gemini) is configured in{' '}
              <a href="/admin/ai-settings" className="text-blue-400 hover:underline">AI Settings</a>.
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <span className="text-sm font-medium">Customer Segmentation</span>
                <p className="text-xs text-zinc-500">Recalculate all customer segments based on current rules</p>
              </div>
              <Button size="sm" variant="outline" onClick={recalcSegments}>Recalculate</Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Shipping Providers */}
      <Card>
        <CardHeader
          title="Shipping Providers"
          icon={<Truck className="h-4 w-4 text-sky-400" />}
          description="Configure shipping providers for automatic shipment creation"
          action={<Button size="sm" variant="outline" onClick={() => setShowAddProvider(!showAddProvider)}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>}
        />
        <CardBody>
          {showAddProvider && (
            <div className="p-3 rounded-lg bg-elevated border border-white/[0.06] mb-3">
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Provider Name</label>
                  <Input value={newProvider.provider_name} onChange={e => setNewProvider(p => ({ ...p, provider_name: e.target.value }))}
                    placeholder="Aramex" className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 block mb-1">Provider Code</label>
                  <Input value={newProvider.provider_code} onChange={e => setNewProvider(p => ({ ...p, provider_code: e.target.value }))}
                    placeholder="aramex" className="h-8 text-xs" />
                </div>
              </div>
              <div className="flex items-center gap-6 mb-2">
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={newProvider.is_enabled} onCheckedChange={v => setNewProvider(p => ({ ...p, is_enabled: v }))} />
                  Enabled
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={newProvider.is_auto_shipping} onCheckedChange={v => setNewProvider(p => ({ ...p, is_auto_shipping: v }))} />
                  Auto-shipping
                </label>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addProvider}><Save className="h-3 w-3 mr-1" /> Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddProvider(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {providers.length === 0 ? (
            <EmptyState title="No shipping providers configured. Add one to enable automatic shipment creation." />
          ) : (
            <div className="space-y-2">
              {providers.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-elevated border border-white/[0.04]">
                  <div className="flex items-center gap-3">
                    <Truck className="h-4 w-4 text-zinc-500" />
                    <div>
                      <span className="text-sm font-medium">{p.provider_name}</span>
                      <span className="text-xs text-zinc-500 ml-2 font-mono">{p.provider_code}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.is_enabled && <span className="text-xs text-emerald-400">Enabled</span>}
                    {p.is_auto_shipping && <span className="text-xs text-sky-400">Auto-ship</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Automation Rules Info */}
      <Card>
        <CardHeader title="Automation Flow" description="Current flow configuration" />
        <CardBody>
          <div className="text-sm text-zinc-400 space-y-1">
            <p><strong>Order Created</strong> → WhatsApp COD confirmation → customer confirms</p>
            <p><strong>Confirmed</strong> → AI collects address data → upsell offers</p>
            <p><strong>Data Completed</strong> → shipping creation (if provider configured)</p>
            <p><strong>Shipped</strong> → delivery notification → campaign scheduling</p>
            <p><strong>Returned</strong> → recovery WhatsApp message → reshipment</p>
            <p className="text-xs text-zinc-600 mt-2">
              Flows require WhatsApp connection + enabled templates. Configure in WhatsApp Settings.
            </p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
